'use client';
import React from 'react';
import { useRouter } from 'next/navigation';
import style from './login.module.css';
import buildUserObj from '../utils/buildUserObj';
import { useLoaderStore, useToastStore } from '../store/localapi';

declare global {
  interface Window {
    // eslint-disable-next-line
    FB: any;
    fbAsyncInit: () => void;
  }
}

// eslint-disable-next-line
export default function FacebookLogin({ handleLoginApi, terms, minage, seterr }: { handleLoginApi: (userObj: any, router: any, setErrors: (err: any) => void, setLoading?: (loading: boolean) => void) => void, terms: boolean, minage: boolean, seterr: (err: any) => void }) {
  const { showToast } = useToastStore();
  const router = useRouter();
  const { showLoader, hideLoader } = useLoaderStore()


  const fetchProfile = async (token: string) => {
    try {
      const url = `https://graph.facebook.com/v19.0/me?fields=id,name,email,picture.type(large)&access_token=${token}`;
      const res = await fetch(url, { cache: 'no-store' });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('Profile fetch failed:', res.status, errorText);
        throw new Error(`profile_fetch_failed: ${res.status}`);
      }

      const profileData = await res.json();
      // console.log('Profile data received:', profileData);

      // Check if we received an error from Facebook API
      if (profileData.error) {
        console.error('Facebook API error:', profileData.error);
        throw new Error(`facebook_api_error: ${profileData.error.message}`);
      }

      // Validate that we have the required fields
      if (!profileData.id || !profileData.name) {
        console.error('Invalid profile data:', profileData);
        throw new Error('invalid_profile_data');
      }
      return profileData;

    } catch (error) {
      console.error('Error in fetchProfile:', error);
      hideLoader();
      throw error;
    }
  };


  const handlelogin = () => {
    // console.log('Starting Facebook login...');
    if (!terms || !minage) {
      seterr({
        minage: !minage ? 'confirm minimum age' : '',
        terms: !terms ? 'agree to terms' : ''
      });
      return;
    }

    if (!window.FB) {
      const errorMsg = 'Facebook login is currently unavailable. Please try again later.';
      seterr({ general: errorMsg });
      showToast(errorMsg, 'error');
      return;
    }

    const processProfile = async (accessToken: string) => {
      try {
        const profileData = await fetchProfile(accessToken);
        if (profileData?.email === undefined) {
          hideLoader();
          showToast('User detail not found.', 'error');
          return
        }
        const userObj = buildUserObj({
          name: profileData.name,
          email: profileData.email,
          picture: profileData.picture?.data?.url,
          login_type: "Facebook"
        });
        // console.log(userObj)
        handleLoginApi(userObj, router, seterr);
      } catch (error) {
        hideLoader();
        console.error('Error processing profile:', error);
        seterr({ general: 'Failed to fetch profile information' });
      }
    };

    try {
      showLoader();
      // eslint-disable-next-line
      window.FB.getLoginStatus((response: any) => {
        // console.log(response)
        if (!response || typeof response.status !== 'string') {
          const errorMsg = 'Facebook authentication failed. Please refresh and try again';
          showToast(errorMsg, 'error');
          hideLoader();
          return;
        }

        if (response.status === 'connected' && response.authResponse?.accessToken) {
          showToast('Connecting with Facebook...', 'info');
          processProfile(response.authResponse.accessToken);
        } else {
          // eslint-disable-next-line
          window.FB.login((loginResponse: any) => {
            if (loginResponse?.authResponse?.accessToken) {
              // console.log('Facebook login successful');
              processProfile(loginResponse.authResponse.accessToken);
            } else {
              let errorMessage = 'Facebook login was cancelled';
              if (loginResponse?.error) {
                switch (loginResponse.error.code) {
                  case 4201: // User cancelled login
                    errorMessage = 'Facebook login was cancelled';
                    break;
                  case 4202: // Duplicate post
                    errorMessage = 'Please try again';
                    break;
                  default:
                    errorMessage = loginResponse.error.message || 'Facebook login failed';
                }
              }
              hideLoader();
              showToast(errorMessage, 'error');
            }
          }, {
            scope: 'email,public_profile',
            return_scopes: true,
            auth_type: 'rerequest' // Re-request if permissions were previously denied
          });
        }
      });
    } catch (error) {
      console.error('Facebook login initialization error:', error);
      const errorMsg = 'Unable to initialize Facebook login. Please refresh the page';
      hideLoader();
      showToast(errorMsg, 'error');
    }
  };


  return (
    <div className={style.LoginsocialIcon} onClick={handlelogin} >
      <svg xmlns="http://www.w3.org/2000/svg" width="31" height="31" viewBox="0 0 31 31" fill="none" role="img" aria-label="Login with Facebook">
        <path d="M15.6839 28.2076C22.779 28.2076 28.5308 22.4558 28.5308 15.3606C28.5308 8.26545 22.779 2.51367 15.6839 2.51367C8.58869 2.51367 2.83691 8.26545 2.83691 15.3606C2.83691 22.4558 8.58869 28.2076 15.6839 28.2076Z" fill="url(#paint0_linear_2499_2059)" />
        <path d="M21.2106 10.7415C21.2171 9.87236 21.206 9.00174 21.2103 8.13262C20.564 8.05854 19.9162 7.95056 19.2661 7.90686C16.7415 7.73748 14.5262 8.43132 13.6342 10.9924C13.3193 11.8966 13.3654 12.954 13.3583 13.9024C13.3537 14.5041 13.3617 15.107 13.3583 15.7087H10.1471C10.1465 16.1934 10.1478 16.6792 10.1471 17.1638C10.146 17.8538 10.149 18.5445 10.1458 19.2348C10.1403 19.2605 10.1639 19.3213 10.1848 19.3213H13.3583C13.359 19.6721 13.3573 20.0242 13.3583 20.375C13.3613 21.3781 13.3579 22.3824 13.3583 23.3855C13.3589 24.9918 13.3552 27.9458 13.3552 27.9956C13.3552 28.0453 14.7955 28.206 15.5156 28.206C16.4706 28.227 17.5981 28.0698 17.5981 28.0642C17.5981 27.9049 17.5974 25.1931 17.5979 23.6866C17.5983 22.4829 17.5949 21.2778 17.5979 20.074C17.5985 19.8235 17.5974 19.5719 17.5979 19.3214H20.3951C20.4211 19.3214 20.4447 19.2264 20.4507 19.2015C20.6494 18.3952 20.75 17.3529 20.8843 16.5117C20.9268 16.2446 20.9639 15.9754 21.0097 15.7089H17.5979C17.6061 15.0154 17.5868 14.3199 17.5979 13.6266C17.6038 13.2523 17.5833 12.8628 17.6855 12.4974C18.1041 11.0009 20.0158 11.5158 21.1694 11.4154C21.2019 11.4069 21.2053 11.3847 21.2106 11.3562C21.2378 11.2088 21.2093 10.9102 21.2106 10.7415Z" fill="#FEFEFF" />
        <defs>
          <linearGradient id="paint0_linear_2499_2059" x1="15.3333" y1="3.09313" x2="16.0351" y2="27.6557" gradientUnits="userSpaceOnUse">
            <stop stopColor="#44A7DE" />
            <stop offset="1" stopColor="#3665B0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}